import React, { createContext, useContext, useState, useCallback } from 'react';

// Region-specific data — drives content across the site.
export const REGIONS = {
  in: {
    code: 'in',
    label: 'India',
    jurisdiction: 'India',
    privacyLaw: 'DPDP Act 2023',
    office: 'KOIKKARA BUILDING MAYILADUMKUNNU, P.O, Kizhakkambalam, Kerala 683562, India',
    email: 'info@proveninfosec.com',
    phone: '+91 484 000 0000',
    map: 'https://maps.app.goo.gl/ZEV1ThCeXsNj1wYv7',
    // Interactive embedded map (keyless), aligned to the "Proven Infosec"
    // Google listing at Kizhakkambalam. To repoint later, update q=<lat,lng>.
    mapEmbed: 'https://www.google.com/maps?q=10.036823,76.4192006&z=16&hl=en&output=embed',
    // India regulatory frameworks (Proven InfoSec content — Section 6)
    frameworks: [
      'ISO/IEC 27001:2022',
      'DPDP Act 2023',
      'IT Act 2000 (+2008)',
      'RBI Cybersecurity Framework',
      'SEBI CSCRF 2024',
      'NCIIPC Guidelines',
    ],
  },
  uk: {
    code: 'uk',
    label: 'United Kingdom',
    jurisdiction: 'United Kingdom',
    privacyLaw: 'UK GDPR',
    office: 'Unit 5, Mansfield House, 22 Northgate, Sleaford, NG34 7DA, United Kingdom',
    email: 'info@proveninfosec.co.uk',
    phone: '+44 1529 000000',
    map: 'https://maps.app.goo.gl/N3PRbAVFXbJQiQsw7',
    // Interactive embedded map (keyless), pointed at the Mansfield House
    // building. To repoint later, update q=<lat,lng>.
    mapEmbed: 'https://www.google.com/maps?q=53.0005826,-0.4102102&z=16&hl=en&output=embed',
    // UK regulatory frameworks (Proven InfoSec content — Section 6)
    frameworks: [
      'ISO/IEC 27001:2022',
      'Cyber Essentials & CE+',
      'UK GDPR & DPA 2018',
      'NIS2 / NIS Regulations 2024',
      'FCA Operational Resilience',
      'NCSC CAF',
    ],
  },
};

const RegionContext = createContext(undefined);

const REGION_HOSTS = {
  in: 'proveninfosec.com',
  uk: 'proveninfosec.co.uk',
};

function productionRegion(hostname) {
  const host = (hostname || '').toLowerCase();
  if (host === REGION_HOSTS.uk || host === `www.${REGION_HOSTS.uk}`) return 'uk';
  if (host === REGION_HOSTS.in || host === `www.${REGION_HOSTS.in}`) return 'in';
  return null;
}

export function regionUrl(nextRegion, location = window.location) {
  if (!productionRegion(location.hostname)) return null;

  const useWww = location.hostname.toLowerCase().startsWith('www.');
  const hostname = `${useWww ? 'www.' : ''}${REGION_HOSTS[nextRegion]}`;
  return `${location.protocol}//${hostname}${location.pathname}${location.search}${location.hash}`;
}

// Resolve the active region. A proveninfosec.co.uk host always locks to UK;
// otherwise the stored preference wins, defaulting to India. This supports the
// two-domain plan (proveninfosec.co.uk = UK site, proveninfosec.com = India),
// and is used both for the initial region and to tag form submissions.
export function currentRegion() {
  if (typeof window === 'undefined') return 'in';
  const liveRegion = productionRegion(window.location.hostname);
  if (liveRegion) return liveRegion;
  try {
    return window.localStorage.getItem('region') || 'in';
  } catch (e) {
    return 'in';
  }
}

export function RegionProvider({ children }) {
  const [region, setRegionState] = useState(() => currentRegion());
  const [transitioning, setTransitioning] = useState(false);

  const setRegion = useCallback((next) => {
    const destination = regionUrl(next);
    if (destination) {
      window.location.assign(destination);
      return;
    }

    setRegionState((prev) => {
      if (prev === next) return prev;
      try {
        window.localStorage.setItem('region', next);
      } catch (e) {
        /* ignore */
      }
      // Trigger the sweep animation
      setTransitioning(true);
      window.setTimeout(() => setTransitioning(false), 1900);
      return next;
    });
  }, []);

  const toggleRegion = useCallback(() => {
    setRegion(region === 'in' ? 'uk' : 'in');
  }, [region, setRegion]);

  return (
    <RegionContext.Provider
      value={{ region, setRegion, toggleRegion, data: REGIONS[region], transitioning }}
    >
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const ctx = useContext(RegionContext);
  if (!ctx) {
    // Safe fallback so components don't crash if used outside the provider.
    return { region: 'in', setRegion: () => {}, toggleRegion: () => {}, data: REGIONS.in, transitioning: false };
  }
  return ctx;
}
